import React, { useState } from 'react';
import Hero from '../components/Hero';
import SEOHelmet from '../components/SEOHelmet';
import { metaTags, businessData } from '../components/seoConfig';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight, FiX } from 'react-icons/fi';
import { Blob } from '../components/Blob';
import { Images } from '../utils/cloudflareImages';
import BoxBraids from '../img/gigi-braids-gallery-05-twists.jpeg';
import Cornrows from '../img/gigi-braids-gallery-04-cornrows.jpeg';
import Goddess from '../img/gigi-braids-gallery-02-goddess-braids.jpg';
import braids from '../img/gigihairbraidingnewarknewjerseyclient.jpg';

export const Home: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const stats = [
    { number: '10+', label: 'YEARS DOING HAIR' },
    { number: '5000+', label: 'HAPPY CLIENTS' },
    { number: '2010', label: 'STARTED' },
    { number: '100+', label: 'DIFFERENT STYLES' },
  ];

  return (
    <>
      <SEOHelmet
        title={metaTags.home.title}
        description={metaTags.home.description}
        keywords={metaTags.home.keywords}
      />

      {/* Hero Slider */}
      <Hero />

      {/* Our Story Section */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-white relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <Blob
          variant={2}
          className="-z-10 right-0 top-20 w-96 h-96 md:w-[500px] md:h-[500px]"
          style={{ opacity: 0.05 }}
        />
        <Blob
          variant={3}
          className="-z-10 left-0 bottom-20 w-80 h-80 md:w-96 md:h-96"
          style={{ opacity: 0.04 }}
        />
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Side */}
            <div className="relative">
              <div className="relative">
                <img
                  src={Images.heroSlide1}
                  alt="GiGi - Your Braiding Expert"
                  className="w-full h-[600px] object-cover rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 border-4 border-salon-rose/40 -translate-x-4 -translate-y-4 -z-10 rounded-2xl"></div>
              </div>
            </div>

            {/* Text Side */}
            <div className="space-y-6">
              <div className="inline-block">
                <img
                  src={require('../img/logo.png')}
                  alt="Braiding Icon"
                  className="w-16 h-16 object-contain opacity-60"
                />
              </div>

              <h2 className="font-serif text-5xl md:text-6xl text-[#0B0B0B] leading-tight">
                Our Story
              </h2>

              <h3 className="text-2xl md:text-3xl text-[#C97B63] font-medium">
                Right Here in {businessData.city} - Quality Braiding You Can
                Trust
              </h3>

              <div className="space-y-4 text-lg text-[#0B0B0B]/80 leading-relaxed">
                <p>
                  Started in 2010, <strong>GiGi Hair Braiding</strong> is all
                  about making you look and feel amazing. We're your go-to spot
                  in {businessData.city} for beautiful braids that last and keep
                  your hair healthy.
                </p>

                <p>
                  When you come in, we'll sit down and talk about what you want.
                  Whether it's box braids, cornrows, twists, or something
                  special, we got you! We take our time to do it right, and our
                  salon is clean, comfortable, and always welcoming.
                </p>

                <p>
                  Need braids for a special occasion? Just want a fresh new
                  look? Or keeping up with your protective style? Come through!
                  We'll hook you up with braids that turn heads.
                </p>
              </div>

              <p className="text-[#6B6970] italic">
                We do consultations, all types of protective styles, and help
                you keep your hair looking fresh.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Fullscreen Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="relative max-w-7xl w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-20 bg-white hover:bg-gray-100 rounded-full p-3 transition-colors shadow-lg"
                aria-label="Close fullscreen"
              >
                <FiX size={24} className="text-[#0B0B0B]" />
              </button>

              {/* Image */}
              <img
                src={selectedImage}
                alt="Fullscreen view"
                className="max-w-full max-h-full w-auto h-auto object-contain shadow-2xl rounded-xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Section */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-white relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <Blob
          variant={4}
          className="-z-10 right-10 top-10 w-72 h-72 md:w-80 md:h-80"
          style={{ opacity: 0.06 }}
        />
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 },
              },
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <p className="font-serif text-5xl md:text-6xl text-[#0B0B0B] mb-2">
                  {stat.number}
                </p>
                <p className="text-sm tracking-widest uppercase text-[#C97B63]">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Our Salon Section */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-[#FAF8F4] relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <Blob
          variant={1}
          className="-z-10 left-10 top-32 w-96 h-96"
          style={{ opacity: 0.05 }}
        />
        <Blob
          variant={3}
          className="-z-10 right-0 bottom-20 w-[400px] h-[400px]"
          style={{ opacity: 0.04 }}
        />
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl md:text-6xl text-[#0B0B0B] mb-6 leading-tight">
              What We Do
            </h2>
            <p className="text-xl text-[#6B6970] max-w-3xl mx-auto leading-relaxed">
              Expert Braiding for Every Style and Occasion
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Left Content */}
            <div className="space-y-6">
              <p className="text-lg text-[#0B0B0B]/80 leading-relaxed">
                We use the best products and stay up on all the latest braiding
                techniques. From classic box braids to intricate patterns, we do
                it all. All hair types and textures are welcome here!
              </p>

              <p className="text-lg text-[#0B0B0B]/80 leading-relaxed">
                We specialize in protective styles that keep your hair healthy
                while looking fly. Open throughout the week with flexible hours.
                Walk-ins welcome, but if you want to make sure we got time for
                you, book ahead!
              </p>

              <div className="bg-white border-l-4 border-[#C97B63] p-6 rounded-2xl shadow-sm">
                <h3 className="font-serif text-2xl text-[#0B0B0B] mb-3">
                  We'll Work With You
                </h3>
                <p className="text-[#6B6970] leading-relaxed">
                  Every head is different, so we take time to understand what
                  you want and what works best for your hair. Whether you're new
                  to braids or a regular, we'll make sure you leave happy.
                </p>
              </div>
            </div>

            {/* Right Images */}
            <div className="grid grid-cols-2 gap-4">
              <img
                src={BoxBraids}
                alt="Box Braids"
                className="w-full h-64 object-cover rounded-2xl shadow-sm"
              />
              <img
                src={Cornrows}
                alt="Cornrows"
                className="w-full h-64 object-cover rounded-2xl shadow-sm mt-8"
              />
              <img
                src={Goddess}
                alt="Goddess Braids"
                className="w-full h-64 object-cover rounded-2xl shadow-sm -mt-8"
              />
              <img
                src={braids}
                alt="Fresh Braids"
                className="w-full h-64 object-cover rounded-2xl shadow-sm"
              />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Services Preview with Accordion Style */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-white relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <Blob
          variant={2}
          className="-z-10 right-20 top-40 w-80 h-80"
          style={{ opacity: 0.05 }}
        />
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl md:text-6xl text-[#0B0B0B] mb-6 leading-tight">
              What We Offer
            </h2>
            <p className="text-xl text-[#6B6970]">Quality Braiding Services</p>
          </div>

          <div className="space-y-4">
            {[
              {
                title: 'What kind of braids do you do?',
                content:
                  'We do all types! Box Braids, Cornrows, Goddess Braids, Feed-In Braids, Twists, and more. We also do braid maintenance to keep your style looking fresh. All done with quality products and care for your hair.',
              },
              {
                title: 'Can you work with my hair?',
                content:
                  "Yes! We work with all hair types and textures. When you come in, we can talk about what style will work best for you and your hair. New to braids? No problem - we'll walk you through everything.",
              },
              {
                title: 'Why choose GiGi Hair Braiding?',
                content:
                  "Over 10 years of experience, we know what we're doing. We care about your hair health, not just making it look good. Plus, we're located right here in the community and treat everyone like family.",
              },
            ].map((item, index) => (
              <details
                key={index}
                className="bg-white rounded-2xl shadow-sm overflow-hidden group border border-[#E8DDD8]"
              >
                <summary className="px-8 py-6 cursor-pointer flex justify-between items-center text-xl font-medium text-[#0B0B0B] hover:bg-[#FAF8F4] transition-colors">
                  <span>{item.title}</span>
                  <span className="text-[#C97B63]">+</span>
                </summary>
                <div className="px-8 pb-6 text-[#6B6970] leading-relaxed">
                  {item.content}
                </div>
              </details>
            ))}
          </div>

          <motion.div
            className="text-center mt-12"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-[#0B0B0B] text-white px-10 py-4 rounded-2xl font-medium hover:shadow-lg transition-all"
            >
              SEE ALL SERVICES
              <FiArrowRight />
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* Gallery Preview */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-[#FAF8F4] relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <Blob
          variant={4}
          className="-z-10 left-0 top-20 w-96 h-96"
          style={{ opacity: 0.05 }}
        />
        <Blob
          variant={1}
          className="-z-10 right-10 bottom-32 w-72 h-72"
          style={{ opacity: 0.04 }}
        />
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl md:text-6xl text-[#0B0B0B] mb-6 leading-tight">
              Check Out Our Work
            </h2>
            <p className="text-xl text-[#6B6970]">See What We Can Do For You</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[BoxBraids, Cornrows, Goddess].map((img, index) => (
              <motion.div
                key={index}
                className="relative group overflow-hidden aspect-square cursor-pointer rounded-2xl"
                onClick={() => setSelectedImage(img)}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src={img}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-salon-charcoal/0 group-hover:bg-salon-charcoal/30 transition-all"></div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/gallery"
              className="inline-flex items-center gap-3 border-2 border-[#0B0B0B] text-[#0B0B0B] px-10 py-4 rounded-2xl font-medium hover:bg-[#0B0B0B] hover:text-white transition-all group relative overflow-hidden"
            >
              {/* Animated gradient background */}
              <span className="relative bg-gradient-to-r from-[#C97B63] via-[#E8A87C] to-[#C97B63] bg-clip-text text-transparent animate-gradient bg-[length:200%_100%] group-hover:from-white group-hover:via-white group-hover:to-white font-semibold text-lg">
                SEE MORE STYLES
              </span>

              <motion.span
                className="relative"
                animate={{
                  x: [0, 5, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <FiArrowRight className="w-5 h-5 text-[#C97B63] group-hover:text-white transition-colors" />
              </motion.span>
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-[#0B0B0B]"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-6 leading-tight">
            Ready For A New Look?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Book your appointment today and let's get your hair looking amazing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block bg-white text-[#0B0B0B] px-10 py-4 rounded-2xl font-medium hover:shadow-lg transition-all"
            >
              BOOK NOW
            </Link>
            <a
              href={`tel:${businessData.phone}`}
              className="inline-block border-2 border-white text-white px-10 py-4 rounded-2xl font-medium hover:bg-white hover:text-[#0B0B0B] transition-all"
            >
              CALL {businessData.phone}
            </a>
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default Home;
